import React from 'react';
import style from './styles.module.css';

interface BreadcrumbItem {
  name: string;
  path?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

export interface BreadcrumbsProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  className?: string;
  underline?: 'hover' | 'always' | 'none';
  separator?: string | React.ReactNode;
  showBackButton?: boolean;
  backLabel?: string;
  backHref?: string;
  onBackClick?: React.MouseEventHandler<HTMLAnchorElement>;
  tone?: 'default' | 'inverted' | 'muted';
}

export const Breadcrumbs = ({
  items,
  className = '',
  underline = 'hover',
  separator = '/',
  showBackButton,
  backLabel = 'Voltar',
  backHref,
  onBackClick,
  tone = 'default',
  ...props
}: BreadcrumbsProps) => {
  const previousItem = items.length > 1 ? items[items.length - 2] : undefined;
  const backTarget = backHref ?? previousItem?.path;

  const handleBackClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    onBackClick?.(event);
    if (event.defaultPrevented) {
      return;
    }

    if (!backTarget) {
      event.preventDefault();
      if (typeof window !== 'undefined') {
        if (window.history.length > 1) {
          window.history.back();
        } else {
          window.location.assign('/');
        }
      }
    }
  };

  const shouldRenderBack =
    (showBackButton ?? Boolean(backTarget)) && (backTarget || onBackClick);

  return (
    <nav
      className={`${style.breadcrumbsContainer} ${
        style[`tone-${tone}`]
      } ${className}`}
      aria-label="breadcrumb"
      {...props}
    >
      {shouldRenderBack && (
        <div className={style.backRow}>
          <a
            href={backTarget ?? '#'}
            className={style.backButton}
            aria-label={`${backLabel} para a página anterior`}
            onClick={handleBackClick}
          >
            <span className={style.backIcon} aria-hidden="true">
              ‹
            </span>
            <span className={style.backText}>{backLabel}</span>
          </a>
        </div>
      )}

      <div className={style.breadcrumbList}>
        {items.map(({ name, path, onClick }, index) => {
          const isLastItem = index === items.length - 1;

          return (
            <span
              key={`${name}-${index}`}
              className={isLastItem ? style.current : style.breadcrumbItem}
            >
              {path && !isLastItem ? (
                <a
                  href={path}
                  className={`${style.breadcrumbLink} ${style[underline]}`}
                  onClick={onClick}
                  data-testid={`breadcrumb-link-${name
                    .toLowerCase()
                    .replace(/ /g, '-')}`}
                >
                  {name}
                </a>
              ) : (
                <span>{name}</span>
              )}
              {!isLastItem && (
                <span className={style.separator}>{separator}</span>
              )}
            </span>
          );
        })}
      </div>
    </nav>
  );
};
